import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations, getDirForLang, LANGUAGES } from "../lib/translations";

const LangContext = createContext(null);

// Try to derive language from URL path (e.g. /en, /he, /ru)
function langFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  if (seg && translations[seg]) return seg;
  return null;
}

function detectBrowserLang() {
  try {
    const stored = localStorage.getItem("hla_lang");
    if (stored && translations[stored]) return stored;
  } catch {
    /* ignore */
  }
  const navLang = (
    navigator.language ||
    navigator.userLanguage ||
    "en"
  ).toLowerCase();
  if (navLang.startsWith("he") || navLang.startsWith("iw")) return "he";
  if (navLang.startsWith("ru")) return "ru";
  return "en";
}

export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [lang, setLangState] = useState(() => {
    return langFromPath(location.pathname) || detectBrowserLang();
  });

  // Persist + update <html dir/lang>
  useEffect(() => {
    try {
      localStorage.setItem("hla_lang", lang);
    } catch {
      /* ignore */
    }
    const dir = getDirForLang(lang);
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [lang]);

  // Keep lang in sync when URL changes (e.g. user navigates /en → /ru)
  useEffect(() => {
    const fromUrl = langFromPath(location.pathname);
    if (fromUrl && fromUrl !== lang) setLangState(fromUrl);
  }, [location.pathname, lang]);

  // Set language AND update URL accordingly
  const setLang = useCallback(
    (next) => {
      if (!translations[next]) return;
      setLangState(next);
      // If we're on a language-prefixed root page, swap the prefix
      const currentSeg = location.pathname.split("/").filter(Boolean)[0];
      if (currentSeg && translations[currentSeg]) {
        const rest = location.pathname.split("/").slice(2).join("/");
        const target = `/${next}${rest ? "/" + rest : ""}`;
        navigate(target, { replace: false });
      } else if (location.pathname === "/" || location.pathname === "") {
        navigate(`/${next}`, { replace: false });
      }
      // Otherwise (e.g. /app routes) — just update state, don't touch URL
    },
    [location.pathname, navigate],
  );

  const t = useMemo(() => {
    const dict = translations[lang] || translations.en;
    return (path) => {
      const keys = path.split(".");
      let cur = dict;
      for (const k of keys) {
        if (cur && typeof cur === "object" && k in cur) cur = cur[k];
        else return path;
      }
      return cur;
    };
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, languages: LANGUAGES }),
    [lang, t, setLang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
};
